/**
 * Shape a `KnowledgeGraphResponse`'s nodes into the Knowledge Archive's
 * hierarchy: subject sections (first domain tag) containing lesson groups
 * (first source lesson) containing concepts.
 *
 * Both "firsts" are deliberate flattenings: a concept tagged into several
 * domains or taught by several lessons appears exactly once, under its primary
 * home, because a view that repeats concepts reads as more knowledge than the
 * student has. Cross-links stay the job of the concept detail sidebar.
 *
 * Each subject also carries a FLAT `concepts` list. The subject card's dot
 * texture draws one square per concept, and deriving that from the same pass
 * is what guarantees the texture can never disagree with the printed count.
 *
 * Pure and table-testable; the page passes lesson titles in from
 * `useSourceLessonOptions`.
 */

import type { KnowledgeGraphNode } from '@/lib/api-client';
import {
  memoryTier,
  type MemoryTier,
  MEMORY_TIER_ORDER,
} from '@/lib/mindspace/memoryTier';

export const UNGROUPED_SUBJECT = 'Other topics';
export const UNGROUPED_LESSON = 'Standalone concepts';
export const UNTITLED_LESSON = 'Untitled lesson';

export interface ArchiveConcept {
  node: KnowledgeGraphNode;
  tier: MemoryTier;
}

export interface LessonGroup {
  /** Null for concepts with no source lesson. */
  id: string | null;
  title: string;
  concepts: ArchiveConcept[];
}

export interface SubjectGroup {
  name: string;
  count: number;
  tierCounts: Record<MemoryTier, number>;
  lessons: LessonGroup[];
  /** Every concept in the subject, texture order (tier, then importance). */
  concepts: ArchiveConcept[];
}

/**
 * A concept's primary subject, for the subject SECTIONS.
 *
 * Not what the graph clusters by any more — see `lessonKeyOf` for why.
 */
export function subjectOf(node: KnowledgeGraphNode): string {
  return node.domain_tags?.[0] ?? node.category ?? UNGROUPED_SUBJECT;
}

/**
 * What the graph clusters by: the lesson a concept came from.
 *
 * `domain_tags[0]` was the obvious choice and it does not survive real data.
 * Measured on a live 3,109-concept archive: **2,603 distinct tags, 1,099
 * distinct first-tags, 649 of them held by a single concept.** They are
 * LLM-derived keywords unioned across lessons, so the first one is an artifact
 * of which lesson synced first — `ryb`, `mt`, `atp` are not subjects, and a
 * "cluster" of one concept is not a cluster.
 *
 * Lessons halve the group count (1,099 -> 315), carry titles a student wrote
 * or chose, and hold far more of the edges: on the 980 concepts the page
 * actually loads, 774 links fall inside a lesson against 488 inside a tag. For
 * a view whose resting state draws only intra-cluster edges, that difference
 * is most of whether the screen has anything on it.
 *
 * Returns the lesson **id**, never the title. The title arrives from a
 * separate query (`useSourceLessonOptions`) and keying on it would move every
 * node the moment that query resolved. `MindspaceGraph` takes a `subjectLabels`
 * map and resolves the name at paint time instead.
 *
 * A concept in several lessons takes the first, with the same arbitrariness
 * `subjectOf` has — but a lesson is a real thing the student made, so the
 * grouping is defensible even when the choice between two is not.
 */
export function lessonKeyOf(node: KnowledgeGraphNode): string {
  return node.source_lessons?.[0] ?? UNGROUPED_SUBJECT;
}

const emptyTierCounts = (): Record<MemoryTier, number> =>
  Object.fromEntries(MEMORY_TIER_ORDER.map(t => [t, 0])) as Record<
    MemoryTier,
    number
  >;

/** Importance desc (unknown last), name as the tiebreak. */
function byImportance(a: ArchiveConcept, b: ArchiveConcept): number {
  const ai = a.node.importance ?? -1;
  const bi = b.node.importance ?? -1;
  return bi - ai || a.node.name.localeCompare(b.node.name);
}

/**
 * Texture order: tier first (MEMORY_TIER_ORDER runs healthy → needs-you), then
 * importance. Sorting by tier is what makes a block of squares read as a
 * gradient instead of visual noise — the eye gets one boundary to find rather
 * than N scattered colour changes.
 */
function byTierThenImportance(a: ArchiveConcept, b: ArchiveConcept): number {
  const at = MEMORY_TIER_ORDER.indexOf(a.tier);
  const bt = MEMORY_TIER_ORDER.indexOf(b.tier);
  return at - bt || byImportance(a, b);
}

export function groupConcepts(
  nodes: readonly KnowledgeGraphNode[],
  lessonTitles: ReadonlyMap<string, string>,
  now: Date = new Date()
): SubjectGroup[] {
  const subjects = new Map<string, Map<string | null, ArchiveConcept[]>>();

  for (const node of nodes) {
    const subject = subjectOf(node);
    const lessonId = node.source_lessons?.[0] ?? null;
    const lessons = subjects.get(subject) ?? new Map();
    subjects.set(subject, lessons);
    const list = lessons.get(lessonId) ?? [];
    lessons.set(lessonId, list);
    list.push({ node, tier: memoryTier(node, now) });
  }

  const groups: SubjectGroup[] = [...subjects.entries()].map(
    ([name, lessons]) => {
      const lessonGroups: LessonGroup[] = [...lessons.entries()].map(
        ([id, concepts]) => ({
          id,
          title:
            id == null
              ? UNGROUPED_LESSON
              : (lessonTitles.get(id) ?? UNTITLED_LESSON),
          concepts: concepts.sort(byImportance),
        })
      );
      lessonGroups.sort(
        (a, b) =>
          b.concepts.length - a.concepts.length ||
          a.title.localeCompare(b.title)
      );

      const tierCounts = emptyTierCounts();
      const flat: ArchiveConcept[] = [];
      for (const lesson of lessonGroups) {
        for (const concept of lesson.concepts) {
          tierCounts[concept.tier] += 1;
          flat.push(concept);
        }
      }
      flat.sort(byTierThenImportance);

      return {
        name,
        count: flat.length,
        tierCounts,
        lessons: lessonGroups,
        concepts: flat,
      };
    }
  );

  return groups.sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name)
  );
}
